/**
 * Citation anchor helpers (Task 144). The settled answer's `[n]` markers become
 * chips linked to that answer's nth source card. Anchors are scoped per message
 * so they resolve correctly across multi-turn (and on desktop, where the ambient
 * SourceRail only shows the latest answer).
 */

/** Sanitize a message id for use in DOM ids / hrefs (drop ":" etc). */
function cleanId(messageId: string): string {
  return messageId.replace(/[^a-zA-Z0-9_-]/g, "-");
}

/**
 * Stable identity for a source (path + timestamps), used to match a clicked
 * citation chip against the SourceRail card showing the SAME source — so the
 * highlight is correct by identity, not by list index (Task 144).
 */
export function sourceKey(citation: {
  path: string;
  timestamp_start?: number;
  timestamp_end?: number;
}): string {
  const start = typeof citation.timestamp_start === "number" ? citation.timestamp_start : "";
  const end = typeof citation.timestamp_end === "number" ? citation.timestamp_end : "";
  return `${citation.path}::${start}-${end}`;
}

/** DOM id for the nth source card of an answer (1-based n). */
export function citeAnchorId(messageId: string, n: number): string {
  return `camino-cite-${cleanId(messageId)}-${n}`;
}

/** DOM id for an answer's inline-sources wrapper. */
export function sourcesWrapId(messageId: string): string {
  return `camino-sources-${cleanId(messageId)}`;
}

export const CITE_HREF_PREFIX = "#camino-cite-";

/** Markdown link href a `[n]` marker is rewritten to. */
export function citeHref(messageId: string, n: number): string {
  return `#${citeAnchorId(messageId, n)}`;
}

/** Parse a cite href back into the 1-based source number (null if not a cite). */
export function parseCiteHref(href: string): number | null {
  if (!href.startsWith(CITE_HREF_PREFIX)) return null;
  const match = /-(\d+)$/.exec(href);
  if (match === null) return null;
  const n = Number(match[1]);
  return Number.isInteger(n) && n >= 1 ? n : null;
}

/**
 * Rewrite in-range `[n]` markers into Markdown links to in-answer anchors so the
 * Markdown renderer turns them into citation chips. Only `[n]` with 1<=n<=count
 * and NOT immediately followed by `(` (which would be a real Markdown link) are
 * rewritten; everything else stays literal text. No-op when count is 0, so the
 * transform is safe before the prompt rollout.
 */
export function linkifyCitations(text: string, messageId: string, count: number): string {
  if (count <= 0) return text;
  return text.replace(/\[(\d+)\](?!\()/g, (whole, num: string) => {
    const n = Number(num);
    if (n >= 1 && n <= count) return `[${n}](${citeHref(messageId, n)})`;
    return whole;
  });
}

/**
 * Reveal + scroll to + briefly flash the cited source card. Works on desktop
 * (where the inline sources are normally hidden — we reveal them on demand) and
 * mobile, for any turn. Imperative DOM work invoked from a click handler.
 */
export function revealCitation(messageId: string, n: number): void {
  if (typeof document === "undefined") return;
  const wrap = document.getElementById(sourcesWrapId(messageId));
  if (wrap !== null) {
    wrap.classList.add("inline-sources-wrap--revealed");
    const details = wrap.querySelector("details");
    if (details instanceof HTMLDetailsElement && !details.open) details.open = true;
  }
  const card = document.getElementById(citeAnchorId(messageId, n));
  if (card !== null) {
    card.scrollIntoView({ behavior: "smooth", block: "center" });
    card.classList.add("source-card--flash");
    // Persistently expand the grounding passage of the card the user jumped to:
    // show its FULL verbatim excerpt instead of the 3-line preview. The flash is
    // transient (removed below); the expansion stays (it's what the reader asked
    // to see). Other cards keep the clamped preview.
    card.classList.add("source-card--expanded");
    window.setTimeout(() => card.classList.remove("source-card--flash"), 1600);
  }
}
